import {
  AccountActiveAction,
  AccountsAction,
  AccountsActionTypes,
} from './accounts.actions';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import {
  AccountCategory,
  AccountState,
  BootstrapStatus,
  IAccount,
  ProAccount,
} from '@account-users/account/models';
import { FormState, IMigration } from '@shared';

export const ACCOUNTS_FEATURE_KEY = 'accounts';

export enum LoadingState {
  Loading,
  Loaded,
  Failed,
  None,
}

export interface State extends EntityState<IAccount> {
  accountCategories: AccountCategory[];
  isLoadingAccounts: boolean;
  createAccountState: FormState;
  status: BootstrapStatus;
  deleteAccountState: FormState;
  sortAccountOnAsc: boolean;
  searchCategories: string[];
  migrations: IMigration[];
  isLoadingMigrations: boolean;
  accountPackageState: AccountState;
  newPackage: string;
  proAccounts: ProAccount[];
  proAccountsIsLoading: LoadingState;
  crmKeyspaceIsCreating?: boolean;
  crmKeyspaceTimeoutFailed?: boolean;
  rebootstrapLicensesIsLoading?: boolean;
  rebootstrapLicensesTimeoutFailed?: boolean;
  cancelScheduleMigrationIsLoading?: boolean;
}

export const initialState: State = {
  ids: [],
  entities: {},
  accountCategories: [],
  isLoadingAccounts: false,
  createAccountState: undefined,
  status: {
    audience: AccountState.Pending,
    adminInvite: AccountState.Pending,
  },
  deleteAccountState: undefined,
  sortAccountOnAsc: false,
  searchCategories: [],
  migrations: [],
  isLoadingMigrations: false,
  accountPackageState: AccountState.Success,
  newPackage: null,
  proAccounts: [],
  proAccountsIsLoading: LoadingState.None,
  crmKeyspaceIsCreating: false,
  crmKeyspaceTimeoutFailed: false,
  rebootstrapLicensesIsLoading: false,
  rebootstrapLicensesTimeoutFailed: false,
  cancelScheduleMigrationIsLoading: false,
};

const adapter: EntityAdapter<IAccount> = createEntityAdapter<IAccount>({
  sortComparer: sortById,
});

export function accountsReducer(
  state: State = initialState,
  action: AccountsAction,
): State {
  switch (action.type) {
    case AccountsActionTypes.LoadAccountCategoriesSuccess: {
      return {
        ...state,
        accountCategories: action.payload,
      };
    }

    case AccountsActionTypes.LoadAccounts: {
      const { categories } = action.payload
        ? action.payload
        : { categories: [] };
      return {
        ...state,
        searchCategories: categories ? categories : [],
        isLoadingAccounts: true,
      };
    }

    case AccountsActionTypes.SortAccounts: {
      return {
        ...state,
        sortAccountOnAsc: !state.sortAccountOnAsc,
      };
    }

    case AccountsActionTypes.LoadAccountsSuccess: {
      return adapter.setAll(action.payload, {
        ...state,
        isLoadingAccounts: false,
      });
    }

    case AccountsActionTypes.LoadAccountMigrations: {
      return {
        ...state,
        isLoadingMigrations: true,
      };
    }
    case AccountsActionTypes.LoadAccountMigrationsSuccess: {
      const { migrations } = action.payload;
      return {
        ...state,
        migrations: migrations,
        isLoadingMigrations: false,
      };
    }
    case AccountsActionTypes.LoadAccountMigrationsFailure: {
      return {
        ...state,
        isLoadingMigrations: false,
      };
    }

    case AccountsActionTypes.PrepareCreateAccount: {
      return {
        ...state,
        createAccountState: FormState.submitting,
      };
    }

    case AccountsActionTypes.CreateAccountFailure: {
      return {
        ...state,
        createAccountState: FormState.failure,
      };
    }

    case AccountsActionTypes.CreateAccountSuccess: {
      return adapter.addOne(action.payload, {
        ...state,
        createAccountState: FormState.success,
      });
    }

    case AccountsActionTypes.AccountBootstrapDone: {
      return adapter.updateOne(
        { id: action.payload.id, changes: action.payload },
        {
          ...state,
          status: {
            ...state.status,
            audience: action.payload.state,
            adminInvite:
              action.payload.state === AccountState.Success
                ? AccountState.Pending
                : AccountState.Failed,
          },
        },
      );
    }

    case AccountsActionTypes.InviteAccountAdminSuccess: {
      return {
        ...state,
        status: {
          ...state.status,
          adminInvite: AccountState.Success,
        },
      };
    }

    case AccountsActionTypes.InviteAccountAdminFailure: {
      return {
        ...state,
        status: {
          ...state.status,
          adminInvite: AccountState.Failed,
        },
      };
    }

    case AccountsActionTypes.ResetCreateAccountState: {
      return {
        ...state,
        createAccountState: undefined,
        status: {
          audience: AccountState.Pending,
          adminInvite: AccountState.Pending,
        },
      };
    }

    case AccountsActionTypes.DeleteAccount: {
      return adapter.removeOne(action.payload, {
        ...state,
        deleteAccountState: FormState.submitting,
      });
    }

    case AccountsActionTypes.DeleteAccountSuccess: {
      return adapter.removeOne(action.payload, {
        ...state,
        deleteAccountState: FormState.success,
      });
    }

    case AccountsActionTypes.DeleteAccountFailure: {
      return {
        ...state,
        deleteAccountState: FormState.failure,
      };
    }

    case AccountsActionTypes.ResetUpdateLicensePackage: {
      return {
        ...state,
        newPackage: null,
        accountPackageState: AccountState.Success,
      };
    }

    case AccountsActionTypes.UpdateLicensePackage: {
      return {
        ...state,
        newPackage: null,
        accountPackageState: AccountState.Pending,
      };
    }

    case AccountsActionTypes.UpdateLicensePackageSuccess: {
      const { licensePackage } = action.payload;
      return {
        ...state,
        newPackage: licensePackage,
        accountPackageState: AccountState.Success,
      };
    }

    case AccountsActionTypes.UpdateLicensePackageFailure: {
      return {
        ...state,
        newPackage: null,
        accountPackageState: AccountState.Failed,
      };
    }

    case AccountsActionTypes.GetProAccounts: {
      return {
        ...state,
        proAccountsIsLoading: LoadingState.Loading,
      };
    }

    case AccountsActionTypes.GetProAccountsSuccess: {
      return {
        ...state,
        proAccountsIsLoading: LoadingState.Loaded,
        proAccounts: action.payload,
      };
    }

    case AccountsActionTypes.GetProAccountsFailure: {
      return {
        ...state,
        proAccountsIsLoading: LoadingState.Failed,
      };
    }

    case AccountsActionTypes.PutProAccountsSuccess:
      return {
        ...state,
        proAccounts: [...(state.proAccounts || []), ...action.payload],
      };

    case AccountsActionTypes.UnlinkProAccountSuccess:
      return {
        ...state,
        proAccounts: (state.proAccounts || []).filter(
          (proAccount) => proAccount.id !== action.payload,
        ),
      };

    case AccountsActionTypes.CreateCrmKeyspace:
      return {
        ...state,
        crmKeyspaceIsCreating: true,
        crmKeyspaceTimeoutFailed: false,
      };

    case AccountsActionTypes.CreateCrmKeyspaceSuccess:
      return {
        ...state,
        crmKeyspaceIsCreating: false,
      };

    case AccountsActionTypes.CreateCrmKeyspaceFailure: {
      return {
        ...state,
        crmKeyspaceIsCreating: false,
      };
    }

    case AccountsActionTypes.CreateCrmKeyspaceTimeoutFailure: {
      return {
        ...state,
        crmKeyspaceIsCreating: false,
        crmKeyspaceTimeoutFailed: true,
      };
    }

    case AccountsActionTypes.RebootstrapLicenses: {
      return {
        ...state,
        rebootstrapLicensesIsLoading: true,
        rebootstrapLicensesTimeoutFailed: false,
      };
    }

    case AccountsActionTypes.RebootstrapLicensesSuccess: {
      return {
        ...state,
        rebootstrapLicensesIsLoading: false,
      };
    }

    case AccountsActionTypes.RebootstrapLicensesFailure: {
      return {
        ...state,
        rebootstrapLicensesIsLoading: false,
      };
    }

    case AccountsActionTypes.RebootstrapLicensesTimeoutFailure: {
      return {
        ...state,
        rebootstrapLicensesIsLoading: false,
        rebootstrapLicensesTimeoutFailed: true,
      };
    }
  }

  return state;
}

function sortById(a: IAccount, b: IAccount): number {
  return a.id.localeCompare(b.id);
}

const { selectAll, selectEntities } = adapter.getSelectors();

export const getAllEntities = selectEntities;

export const getAllAccounts = selectAll;

export const getIsLoadingAccounts = (state: State) => state.isLoadingAccounts;

export const getAccountPackageState = ({
  accountPackageState,
  newPackage,
}) => ({ accountPackageState, newPackage });

export const getIsLoadingMigrations = (state: State) =>
  state.isLoadingMigrations;
export const getAccountMigrations = (state: State) => state.migrations;

export const getCreateAccountState = (state: State) => state.createAccountState;

export const getAccountStatus = (state: State) => state.status;

export const getSortAccountOnAsc = (state: State) => state.sortAccountOnAsc;

export const getDeleteAccountState = (state: State) => state.deleteAccountState;

export const getAccountSearchCategoriesState = (state: State) =>
  state.searchCategories;

export const getIsBootstrapComplete = ({
  status: { adminInvite, audience },
}: State) => {
  return (
    adminInvite !== AccountState.Pending && audience !== AccountState.Pending
  );
};

export const getIsBootstrapFailed = ({
  status: { adminInvite, audience },
}: State) => {
  return (
    adminInvite === AccountState.Failed || audience === AccountState.Failed
  );
};

export const getProAccounts = (state: State) => state.proAccounts;

export const getProAccountsIsLoading = (state: State) =>
  state.proAccountsIsLoading === LoadingState.Loading;

export const getCrmKeyspaceIsLoading = (state: State) =>
  state.crmKeyspaceIsCreating;

export const getCrmKeyspaceTimeoutFailed = (state: State) =>
  state.crmKeyspaceTimeoutFailed;

export const getRebootstrapLicensesIsLoading = (state: State) =>
  state.rebootstrapLicensesIsLoading;

export const getRebootstrapLicensesTimeoutFailed = (state: State) =>
  state.rebootstrapLicensesTimeoutFailed;
